<script lang="ts" setup>
import { ref, type PropType } from 'vue';

import { syncWithStorage } from '@exchange/helpers/persistance-helper';
import useTradingMobileRoutes from '@exchange/libs/composables/shared/src/lib/useTradingMobileRoutes';
import { CONSTANTS } from '@exchange/libs/utils/constants/src';

import MarketList from './components/MarketList.vue';
import MarketListFilters from './components/MarketListFilters.vue';
import useMarketListFilters from './useMarketListFilters';

const props = defineProps({
  searchInFocus: { type: Boolean, default: false },
  variant: { type: String as PropType<'dark-medium' | 'default'>, default: 'default' },
  withLogo: { type: Boolean, default: false },
  marketId: { type: String, default: () => syncWithStorage(CONSTANTS.SELECTED_MARKET, CONSTANTS.DEFAULT_MARKET_ID) },
  isFutures: { type: Boolean, default: false },
});
const emit = defineEmits<{
  (e: 'market-clicked', marketId: string);
}>();

useTradingMobileRoutes(props.isFutures ? { name: 'futures', params: { marketId: props.marketId } } : { name: 'exchange' });

const { filteredMarketList, isFavorite, updateFavorite, marketDataLoading, applyMarketFilters, activeCurrencyFilter, marketTypeFilter } = useMarketListFilters();

applyMarketFilters({});

const search = ref<string>();

const onSearchCallback = (searchString?: string) => applyMarketFilters({ search: searchString });
const onMarketTypeFilterSelected = (marketTypeFilterArg?: string) => applyMarketFilters({ marketTypeFilter: marketTypeFilterArg });
</script>

<template>
  <ot-module-container
    class="markets-container"
    log-name="markets"
    variant="default"
    :class="[`markets-container--${variant}`]"
    :with-bottom-padding="true"
    :loading="marketDataLoading"
  >
    <template #default="{ sectionClass }">
      <div
        class="markets-container__search"
        :class="sectionClass"
      >
        <ot-search
          v-model:value="search"
          variant="dark"
          :autofocus="false"
          :on-search-cb="onSearchCallback"
        />
      </div>
    </template>
    <template #header-left-actions>
      <market-list-filters
        class="markets-container__filters"
        :active-market-type-filter="marketTypeFilter"
        :variant="variant"
        @market-type-filter-selected="onMarketTypeFilterSelected"
      />
    </template>
    <template #main-content>
      <market-list
        v-if="!marketDataLoading"
        class="markets-container__list"
        :filtered-by="activeCurrencyFilter"
        :is-favorite="isFavorite"
        :is-filtered-by-favorites="false"
        :market-id="marketId"
        :market-list="filteredMarketList"
        :update-favorite="updateFavorite"
        :with-logo="withLogo"
        @list:market-clicked="emit('market-clicked', $event)"
      />
    </template>
  </ot-module-container>
</template>

<style lang="scss">
.markets-container {
  &__search {
    flex-shrink: 0;
    padding-bottom: 8px;
  }

  .xs & {
    border: 0;
    background-color: rgb(var(--v-theme-elevation-1));
  }
}

.markets-container--dark-medium {
  --ot-module-container-padding-x: 16px;
  --ot-table-background-color: var(--xmc-background-color);

  box-shadow:
    rgb(var(--v-theme-elevation-0)) 0 1px 2px 0,
    rgb(var(--v-theme-elevation-0)) 0 2px 6px 2px;
}
</style>
